/**
 * Onboarding Wizard — /projects/new
 * From FRONTEND_BUILD_SPEC §3.1: 5-step wizard (country, capacity, intent, location, scope reveal)
 */
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronRight, ChevronLeft, Zap, CheckCircle, Sparkles } from "lucide-react";
import { api, checkupSnapshot, type CheckupSnapshot } from "../lib/api.ts";
import { COUNTRY_META } from "../lib/constants.ts";
import { CountryBadge } from "../components/shared/CountryBadge.tsx";
import { getIntentOptions, getCapacityStep, snapCapacity } from "../lib/checkup-options.ts";

type CountryCode = "KE" | "NG" | "ZA" | "GH" | "NA" | "ZM";

interface WizardState {
  name: string;
  country: CountryCode | null;
  capacity_kw: number | null;
  intent: string;
  location: string;
}

export function OnboardingWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromCheckup = searchParams.get("from") === "checkup";
  const checkupToken = searchParams.get("checkup_token") || undefined;
  const [step, setStep] = useState(0);
  const [state, setState] = useState<WizardState>({
    name: "",
    country: null,
    capacity_kw: 5000,
    intent: "",
    location: "",
  });
  const [creating, setCreating] = useState(false);
  const [scaffolding, setScaffolding] = useState<string[]>([]);
  const [snapshot, setSnapshot] = useState<CheckupSnapshot | null>(null);

  // Fetch checkup snapshot if coming from checkup CTA
  useEffect(() => {
    if (!fromCheckup) return;
    checkupSnapshot.get(checkupToken).then(({ snapshot: snap }) => {
      if (!snap) return;
      setSnapshot(snap);
      setState(prev => ({
        ...prev,
        country: (snap.country as CountryCode) || prev.country,
        capacity_kw: snap.capacity_kw || prev.capacity_kw,
        intent: snap.intent || prev.intent,
        location: snap.fields?.["S-003"] || prev.location,
      }));
    }).catch(() => {});
  }, [fromCheckup, checkupToken]);

  const steps = ["Country", "Capacity", "Intent", "Location", "Scope"];

  function getRoutingResult(): { tier: string; color: string; licenses: number } {
    if (!state.country || !state.capacity_kw) return { tier: "—", color: "gray", licenses: 0 };
    const kw = state.capacity_kw;

    if (state.country === "KE") {
      if (kw <= 1000 && state.intent === "own_use") return { tier: "Exempt", color: "brand", licenses: 2 };
      if (kw <= 1000) return { tier: "Net Metering", color: "warning", licenses: 3 };
      return { tier: "Full License (EPRA)", color: "ai", licenses: 10 };
    }
    if (state.country === "NG") {
      if (kw <= 100) return { tier: "Registration", color: "brand", licenses: 2 };
      if (kw <= 1000) return { tier: "Mini-Grid Permit", color: "warning", licenses: 4 };
      return { tier: "Full License (NERC)", color: "ai", licenses: 8 };
    }
    if (state.country === "ZA") {
      if (state.intent === "private_offtaker") return { tier: "NERSA Registration", color: "brand", licenses: 8 };
      return { tier: "Full License (NERSA)", color: "ai", licenses: 10 };
    }
    if (state.country === "GH") {
      if (kw <= 500) return { tier: "Simplified Permit (EC)", color: "brand", licenses: 4 };
      return { tier: "Wholesale Generation License (EC)", color: "ai", licenses: 12 };
    }
    if (state.country === "NA") {
      if (kw <= 500) return { tier: "Net Metering (ECB)", color: "brand", licenses: 3 };
      return { tier: "Full Licence (ECB)", color: "ai", licenses: 8 };
    }
    if (state.country === "ZM") {
      if (kw < 250 && state.intent === "own_use") return { tier: "Exempt (Own Use)", color: "brand", licenses: 1 };
      if (kw < 250) return { tier: "Net Metering (ERB)", color: "warning", licenses: 3 };
      return { tier: "Full Licence (ERB)", color: "ai", licenses: 8 };
    }
    return { tier: "—", color: "gray", licenses: 0 };
  }

  async function handleCreate() {
    if (!state.country || !state.capacity_kw || !state.name) return;
    setCreating(true);

    const scaffoldSteps = [
      "Creating project record...",
      "Building license graph from seed data...",
      "Resolving field definitions for country...",
      "Generating document checklist...",
      "Associating submission templates...",
      "Computing critical path + parallel workplan...",
      "Running initial evaluation...",
    ];

    // Animate scaffolding
    for (let i = 0; i < scaffoldSteps.length; i++) {
      await new Promise((r) => setTimeout(r, 400));
      setScaffolding((prev) => [...prev, scaffoldSteps[i]!]);
    }

    try {
      const result = await api.createProject({
        name: state.name,
        country_code: state.country,
        capacity_kw: state.capacity_kw,
      });

      // Pre-populate fields from checkup snapshot
      if (snapshot?.mapped_fields) {
        const updates = Object.entries(snapshot.mapped_fields)
          .filter(([id]) => id !== "P-002" && id !== "C-001" && id !== "C-002")
          .map(([field_id, value]) => ({ field_id, value }));
        if (updates.length > 0) {
          await api.batchUpdateFields(result.projectId, updates).catch(() => {});
        }
        await checkupSnapshot.clear(checkupToken).catch(() => {});
      }

      await new Promise((r) => setTimeout(r, 600));
      navigate(`/projects/${result.projectId}`);
    } catch (e) {
      console.error(e);
      setCreating(false);
    }
  }

  const routing = getRoutingResult();

  const intentOptions = state.country && state.capacity_kw
    ? getIntentOptions(state.country, state.capacity_kw)
    : [];

  return (
    <div className="max-w-xl mx-auto px-4 py-8">
      <h1 className="text-[22px] font-bold mb-1" style={{ letterSpacing: "-0.02em" }}>Create New Project</h1>
      <p className="text-[14px] text-text-secondary mb-6">
        Tell us about your solar project and we'll map your regulatory pathway.
      </p>

      {/* Checkup pre-fill banner */}
      {snapshot && (
        <div className="flex items-start gap-2.5 p-3 bg-ai-light border border-ai/20 rounded-xl mb-6">
          <Sparkles size={16} className="text-ai flex-shrink-0 mt-0.5" />
          <p className="text-[13px] text-text-secondary">
            Your checkup answers have been pre-filled. Enter a project name to get started.
          </p>
        </div>
      )}

      {/* Step indicators */}
      <div className="flex items-center gap-1 mb-8 overflow-x-auto pb-1">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center gap-1 flex-shrink-0">
            <div
              className={`w-6 h-6 rounded-full text-[12px] font-medium flex items-center justify-center ${
                i < step ? "bg-brand text-white" : i === step ? "bg-brand text-white" : "bg-surface-tertiary text-text-tertiary"
              }`}
            >
              {i < step ? <CheckCircle size={12} /> : i + 1}
            </div>
            <span className={`text-[13px] hidden sm:inline ${i === step ? "text-text-primary font-medium" : "text-text-tertiary"}`}>
              {s}
            </span>
            {i < steps.length - 1 && <ChevronRight size={12} className="text-text-tertiary mx-1" />}
          </div>
        ))}
      </div>

      {creating ? (
        /* Scaffolding sequence */
        <div className="bg-surface border border-border rounded-2xl p-5">
          <h2 className="text-[17px] font-medium mb-4">Setting up your project...</h2>
          <div className="space-y-2.5">
            {scaffolding.map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-[14px]">
                <CheckCircle size={14} className="text-brand flex-shrink-0" />
                <span className="text-text-secondary">{s}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* Wizard steps */
        <div className="bg-surface border border-border rounded-2xl p-5">
          {/* Step 0: Project name + Country */}
          {step === 0 && (
            <div className="space-y-4">
              <div>
                <label className="block text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-1.5">
                  Project Name
                </label>
                <input
                  type="text"
                  value={state.name}
                  onChange={(e) => setState({ ...state, name: e.target.value })}
                  placeholder="e.g., Naivasha Solar Farm"
                  className="w-full px-3 py-2 border border-border rounded-xl text-[16px] focus:outline-none focus:ring-1 focus:ring-brand/30"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-2">
                  Country
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {(["KE", "NG", "ZA", "GH", "NA", "ZM"] as const).map((code) => (
                    <button
                      key={code}
                      onClick={() => setState({ ...state, country: code })}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-colors ${
                        state.country === code
                          ? "border-brand bg-brand-light"
                          : "border-border hover:border-border-secondary"
                      }`}
                    >
                      <span className="text-2xl">{COUNTRY_META[code].flag}</span>
                      <span className="text-[14px] font-medium">{COUNTRY_META[code].name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Capacity */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-1.5">
                  Plant Capacity
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={state.capacity_kw !== null ? Math.round(state.capacity_kw / 100) / 10 : ""}
                    onChange={(e) => {
                      const val = Math.max(Number(e.target.value), 0.1);
                      setState({ ...state, capacity_kw: Math.round(val * 1000) });
                    }}
                    placeholder="5.0"
                    step={getCapacityStep(state.capacity_kw !== null ? state.capacity_kw / 1000 : 5)}
                    min="0"
                    className="w-32 px-3 py-2 border border-border rounded-xl text-[16px] focus:outline-none focus:ring-1 focus:ring-brand/30"
                  />
                  <span className="text-[16px] text-text-secondary">MW (AC export)</span>
                </div>
              </div>

              {state.capacity_kw && state.country && (
                <div className={`p-3 rounded-xl border text-[14px] ${
                  routing.color === "brand" ? "bg-brand-light border-brand/20 text-brand" :
                  routing.color === "warning" ? "bg-warning-light border-warning/20 text-warning" :
                  "bg-ai-light border-ai/20 text-ai"
                }`}>
                  <div className="font-medium">{routing.tier}</div>
                  <div className="opacity-80 mt-0.5">~{routing.licenses} licenses/permits required</div>
                </div>
              )}

              {state.country === "ZA" && (
                <p className="text-[13px] text-text-tertiary">
                  In South Africa, licensing depends on off-taker type more than capacity — we'll determine this in the next step.
                </p>
              )}
              {state.country === "GH" && (
                <p className="text-[13px] text-text-tertiary">
                  In Ghana, projects ≤500 kW qualify for a simplified Energy Commission permit. Larger projects require a Wholesale Generation License and PURC tariff approval.
                </p>
              )}
            </div>
          )}

          {/* Step 2: Intent */}
          {step === 2 && state.country && (
            <div>
              <label className="block text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-2">
                Intended Use
              </label>
              <div className="space-y-2">
                {intentOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setState({ ...state, intent: opt.value })}
                    className={`w-full text-left px-3 py-2.5 rounded-xl border text-[16px] transition-colors ${
                      state.intent === opt.value
                        ? "border-brand bg-brand-light"
                        : "border-border hover:border-border-secondary"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Location */}
          {step === 3 && (
            <div>
              <label className="block text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-1.5">
                {{ KE: "County", NG: "State", ZA: "Province", GH: "Region", NA: "Region", ZM: "Province" }[state.country || "KE"]}
              </label>
              <input
                type="text"
                value={state.location}
                onChange={(e) => setState({ ...state, location: e.target.value })}
                placeholder={{ KE: "e.g., Nakuru", NG: "e.g., Lagos", ZA: "e.g., Northern Cape", GH: "e.g., Greater Accra", NA: "e.g., Erongo", ZM: "e.g., Lusaka" }[state.country || "KE"]}
                className="w-full px-3 py-2 border border-border rounded-xl text-[16px] focus:outline-none focus:ring-1 focus:ring-brand/30"
              />
              {state.country === "ZA" && state.location.toLowerCase().includes("northern cape") && (
                <div className="mt-2 p-2.5 bg-danger-light border border-danger/20 rounded-xl text-[13px] text-danger">
                  Northern Cape has ZERO available grid capacity since GCCA-2024. Consider Western Cape or Eastern Cape.
                </div>
              )}
            </div>
          )}

          {/* Step 4: Scope Reveal */}
          {step === 4 && (
            <div>
              <h2 className="text-[17px] font-medium mb-3">Your Regulatory Pathway</h2>
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="bg-surface-secondary rounded-xl p-2 sm:p-2.5 text-center">
                  <div className="text-[18px] sm:text-[22px] font-medium">{routing.licenses}</div>
                  <div className="text-[11px] sm:text-[12px] text-text-tertiary uppercase">Licenses</div>
                </div>
                <div className="bg-surface-secondary rounded-xl p-2 sm:p-2.5 text-center">
                  <div className="text-[18px] sm:text-[22px] font-medium">118</div>
                  <div className="text-[11px] sm:text-[12px] text-text-tertiary uppercase">Fields</div>
                </div>
                <div className="bg-surface-secondary rounded-xl p-2 sm:p-2.5 text-center">
                  <div className="text-[18px] sm:text-[22px] font-medium">18-30</div>
                  <div className="text-[11px] sm:text-[12px] text-text-tertiary uppercase">Months Est.</div>
                </div>
              </div>

              {state.country && (
                <CountryBadge code={state.country} showName />
              )}

              <div className="mt-3 text-[14px] text-text-secondary">
                <p className="mb-2">
                  <strong>{state.name}</strong> — {state.capacity_kw ? (state.capacity_kw / 1000).toFixed(1) : 0} MW {routing.tier}
                </p>
                <p>
                  We'll create your project, build the license dependency graph, resolve all field definitions for{" "}
                  {state.country && COUNTRY_META[state.country].name}, and generate your workplan.
                </p>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
            {step > 0 ? (
              <button
                onClick={() => setStep(step - 1)}
                className="flex items-center gap-1 text-[14px] text-text-secondary hover:text-text-primary"
              >
                <ChevronLeft size={14} /> Back
              </button>
            ) : (
              <button
                onClick={() => navigate("/projects")}
                className="text-[14px] text-text-secondary hover:text-text-primary"
              >
                Cancel
              </button>
            )}

            {step < 4 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={
                  (step === 0 && (!state.name || !state.country)) ||
                  (step === 1 && !state.capacity_kw)
                }
                className="flex items-center gap-1 px-4 py-1.5 bg-brand text-white rounded-xl text-[16px] font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next <ChevronRight size={14} />
              </button>
            ) : (
              <button
                onClick={handleCreate}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-brand text-white rounded-xl text-[16px] font-medium"
              >
                <Zap size={14} /> Create project
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
